<template>
    <Row>
        <Cell class="CustomPropertiesManagementTableRow__cellDrag">
            <EidIcon
                :icon-src="faGripVertical"
                alt="drag to reorder"
                class="CustomPropertiesManagementTableRow__dragHandle"
                :class="{ 'CustomPropertiesManagementTableRow__dragHandle--disabled': isSaving }"
            />
        </Cell>
        <Cell class="CustomPropertiesManagementTableRow__cellName" :title="item.name">
            {{ item.name }}
        </Cell>
        <Cell class="CustomPropertiesManagementTableRow__cellDescription" :title="item.description">
            <span v-if="item.description">{{ item.description }}</span>
            <span v-else class="CustomPropertiesManagementTableRow__nullValue">—</span>
        </Cell>
        <Cell>
            <LabelBadge color="info">
                {{ fieldTypeLabel(item.type) }}
            </LabelBadge>
        </Cell>
        <Cell>
            <LabelBadge v-if="item.required" color="danger">
                Required
            </LabelBadge>
        </Cell>
        <Cell class="CustomPropertiesManagementTableRow__cellDisplay">
            <Checkbox
                :model-value="displayed"
                :disabled="displayDisabled"
                @update:model-value="$emit('toggle-display', $event)"
            />
        </Cell>
        <Cell class="CustomPropertiesManagementTableRow__cellActions">
            <div class="CustomPropertiesManagementTableRow__actions">
                <NewButton
                    variant="flat"
                    size="icon-sm"
                    color="neutral"
                    aria-label="edit"
                    @click.stop="$emit('edit')"
                >
                    <EidIcon :icon-src="faPenToSquare" />
                </NewButton>
                <NewButton
                    v-if="showDeleteAction"
                    variant="flat"
                    size="icon-sm"
                    color="danger"
                    aria-label="delete"
                    @click.stop="$emit('delete')"
                >
                    <EidIcon :icon-src="faTrashCan" />
                </NewButton>
                <div
                    v-else
                    class="CustomPropertiesManagementTableRow__actionPlaceholder"
                    aria-hidden="true"
                />
            </div>
        </Cell>
    </Row>
</template>

<style lang="scss">
.CustomPropertiesManagementTableRow {
    &__row--ghost {
        opacity: 0.4;
    }

    &__cellDrag {
        text-align: center;
        width: 40px;
    }

    &__dragHandle {
        color: var(--eidQualityBlack-50);

        &:hover {
            cursor: grab;
        }

        &:active {
            cursor: grabbing;
        }

        &--disabled {
            opacity: 0.4;
            cursor: not-allowed;

            &:hover {
                cursor: not-allowed;
            }
        }
    }

    &__cellName {
        font-size: var(--text-body-size);
        font-weight: var(--text-subheader-weight);
        line-height: var(--text-body-line-height);
        color: var(--eidSecureBlue);
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__cellDescription {
        font-size: var(--text-body-size);
        font-weight: var(--text-body-weight);
        line-height: var(--text-body-line-height);
        color: var(--eidTechGray);
        max-width: 250px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__nullValue {
        font-size: var(--text-body-size);
        color: var(--eidTechGray);
    }

    &__cellDisplay {
        .Checkbox {
            width: fit-content;
            margin: 0 auto;
        }
    }

    &__cellActions {
        text-align: center;
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.25rem;
    }

    // Holds the delete button's slot open on rows that cannot be deleted, so the edit icon
    // lands in the same place on every row instead of re-centring on its own.
    &__actionPlaceholder {
        width: 2.25rem;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faGripVertical, faPenToSquare, faTrashCan } from '@fortawesome/pro-regular-svg-icons';

    import { Checkbox } from '@evidentid/dashboard-commons/components/Checkbox';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { LabelBadge } from '@evidentid/dashboard-commons/components/LabelBadge';
    import { Cell, Row } from '@evidentid/dashboard-commons/components/Table';
    import { CustomProperty, CustomPropertyType } from '@evidentid/tprm-portal-lib/models/dashboard';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { FIELD_TYPE_LABELS }
        from '@/modules/dashboard-configuration/components/custom-properties-management/fieldTypeLabels';

    export default defineComponent({
        name: 'CustomPropertiesManagementTableRow',
        components: {
            Cell,
            Checkbox,
            EidIcon,
            LabelBadge,
            NewButton,
            Row,
        },

        props: {
            item: {
                type: Object as PropType<CustomProperty>,
                required: true,
            },
            displayed: {
                type: Boolean,
                required: true,
            },
            canDelete: {
                type: Boolean,
                default: false,
            },
            displayDisabled: {
                type: Boolean,
                default: false,
            },
            isSaving: {
                type: Boolean,
                default: false,
            },
        },
        emits: [ 'edit', 'delete', 'toggle-display' ],

        data() {
            return {
                faGripVertical,
                faPenToSquare,
                faTrashCan,
            };
        },

        computed: {
            /**
             * The collaterals property drives collateral evaluation for the whole account and can
             * never be deleted. That is decided by type alone, so there is no reason to offer the
             * action and then refuse it — the server enforces the same rule regardless.
             */
            isCollateralsProperty(): boolean {
                return this.item.type === CustomPropertyType.collaterals;
            },
            showDeleteAction(): boolean {
                return this.canDelete && !this.isCollateralsProperty;
            },
        },

        methods: {
            fieldTypeLabel(type: string): string {
                return FIELD_TYPE_LABELS[type] || type;
            },
        },
    });
</script>
